/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import { TextField } from "@mui/material";
import LabelledField from "components/labelled-field";
import { ImageSelector } from "cms/images/image-selector";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import { FeatureAccordionComponentContextWrapper } from "./feature-accordion-context-wrapper";
import { FieldErrorFallback } from "../../../../fields/layout/field-error";
import { ErrorBoundary } from "react-error-boundary";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";

const FeatureAccordionComponentEditor = () => {
    const { token } = useAuth();
    const { component, update, updateFactory } = useUpdateComponent();

    const title = component.title;
    const subtitle = component.subtitle;
    const description = component.description;
    const image_guid = component.image;
    const sections = component.sections || [];
    const showSteps = component.showSteps || false;

    const [image, setImage] = useState(null);

    const onChangeTitle = (event) => {
        const title = event.target.value;
        update("title", title);
    };

    const onChangeSubtitle = (event) => {
        const subtitle = event.target.value;
        update("subtitle", subtitle);
    };

    const onChangeDescription = (richtext) => {
        update("description", richtext);
    };

    const onChangeImage = (image) => {
        update("image", image);
    };

    const onChangeSections = updateFactory("sections");
    const onChangeShowSteps = (event) => {
        const showSteps = event.target.checked;
        update("showSteps", showSteps);
    };
    
    useEffect(() => {
        const getImage = async () => {
            if (image_guid) {
                const image = await SimpliCityAdminSDK.image.findOne(token, image_guid);
                setImage(image);
            }
        };
        getImage();
    }, [image_guid, token]);

    return (
        <div className="feature-accordion-editor-frame">
            <ErrorBoundary
                FallbackComponent={FieldErrorFallback}
                onReset={() => {
                    location.reload(); 
                }}
            >
                <LabelledField label="Title">
                    <TextField
                        id="feature_accordion_title"
                        fullWidth={true}
                        value={title}
                        placeholder="Add a title"
                        onChange={onChangeTitle}
                    />
                </LabelledField>

                <LabelledField label="Subtitle">
                    <TextField
                        id="feature_accordion_subtitle"
                        fullWidth={true}
                        value={subtitle}
                        placeholder="Add a subtitle"
                        onChange={onChangeSubtitle}
                    />
                </LabelledField>

                <LabelledField label="Description">
                    <div className="feature-accordion-description-editor">
                        <SimplicityComposerEnsureTipTap
                            richtext={description}
                            onChange={onChangeDescription}
                        />
                    </div>
                </LabelledField>

                <LabelledField label="Image" isRequired={true}>
                    <div className="feature-accordion-image-selector">
                        <ImageSelector
                            image={image}
                            setImage={onChangeImage}
                            onSelectImage={onChangeImage}
                            onSaveImage={onChangeImage}
                            onImageRemoved={() => onChangeImage(null)}
                            onImageDeleted={() => onChangeImage(null)}
                            onReload={() => {}}
                        />
                    </div>
                </LabelledField>

                <LabelledField label="Accordion">
                    <div className="feature-accordion-accordion-wrapper">
                        <FeatureAccordionComponentContextWrapper
                            sections={sections}
                            showSteps={showSteps}
                            onChangeSections={onChangeSections}
                            onChangeShowSteps={onChangeShowSteps}
                        />
                    </div>
                </LabelledField>
            </ErrorBoundary>
        </div>
    );
};

FeatureAccordionComponentEditor.displayName = "FeatureAccordionComponentEditor";
export { FeatureAccordionComponentEditor }; 
